import React from "react";
import { Box, Flex, HStack, IconButton, Text, useColorModeValue } from "@chakra-ui/react";
import { DeleteIcon } from "@chakra-ui/icons";

export interface NotificationListItemModel {
  id: string;
  title: string;
  /** HTML for non-performance-summary notifications */
  messageHtml?: string;
  /** Plain text preview for performance summary notifications */
  previewText?: string;
  time: string;
  isRead: boolean;
  icon?: React.ReactNode;
}

export interface NotificationListItemProps {
  item: NotificationListItemModel;
  brandColor?: string;
  secondaryTextColor?: string;
  background?: string;
  hoverBackground?: string;
  selected?: boolean;
  selectedBackground?: string;
  showUnreadDot?: boolean;
  showDeleteButton?: boolean;
  onClick?: () => void;
  onDelete?: () => void;
}

export default function NotificationListItem({
  item,
  brandColor,
  secondaryTextColor,
  background,
  hoverBackground,
  selected,
  selectedBackground,
  showUnreadDot = true,
  showDeleteButton = true,
  onClick,
  onDelete,
}: NotificationListItemProps) {
  const fallbackBrand = useColorModeValue("#38BBD1", "#3EEAEC");
  const fallbackSecondary = useColorModeValue("gray.600", "gray.400");
  const modalBg = useColorModeValue("white", "gray.800");
  const hoverBg = useColorModeValue("gray.50", "whiteAlpha.50");
  const selectedBg = useColorModeValue("cyan.50", "whiteAlpha.100");

  const b = brandColor ?? fallbackBrand;
  const s = secondaryTextColor ?? fallbackSecondary;
  const bg = background ?? modalBg;
  const hb = hoverBackground ?? hoverBg;
  const sb = selectedBackground ?? selectedBg;

  return (
    <Box
      px={4}
      py={3}
      cursor={onClick ? "pointer" : "default"}
      bg={selected ? sb : bg}
      _hover={onClick ? { bg: selected ? sb : hb } : undefined}
      onClick={onClick}
    >
      <HStack spacing={3} align="flex-start" flex={1} minW={0}>
        <Box fontSize="20px" flexShrink={0}>
          {item.icon ?? "•"}
        </Box>
        <Flex flex={1} direction="column" minW={0}>
          <HStack spacing={2} mb={1}>
            <Text
              fontSize="sm"
              fontWeight={item.isRead ? "400" : "600"}
              color={b}
              fontFamily="'Urbanist', sans-serif"
            >
              {item.title}
            </Text>
            {!item.isRead && showUnreadDot && (
              <Box w="8px" h="8px" borderRadius="full" bg={b} flexShrink={0} />
            )}
          </HStack>

          {item.previewText ? (
            <Text
              fontSize="xs"
              color={s}
              noOfLines={3}
              fontFamily="'Urbanist', sans-serif"
            >
              {item.previewText}
            </Text>
          ) : (
            <Box
              fontSize="xs"
              color={s}
              noOfLines={2}
              fontFamily="'Urbanist', sans-serif"
              sx={{
                "& p": { margin: 0 },
                "& p + p": { marginTop: "0.25em" },
              }}
              dangerouslySetInnerHTML={{ __html: item.messageHtml || "" }}
            />
          )}

          <Text
            fontSize="xs"
            color={s}
            mt={1}
            fontFamily="'IBM Plex Sans', sans-serif"
          >
            {item.time}
          </Text>
        </Flex>

        {showDeleteButton && onDelete && (
          <IconButton
            aria-label="Delete notification"
            icon={<DeleteIcon />}
            size="xs"
            variant="ghost"
            colorScheme="red"
            flexShrink={0}
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
          />
        )}
      </HStack>
    </Box>
  );
}

